import {
  type AllergenMapping,
  type TaxonomyAllergen,
  TaxonomyDisclaimer,
  type TaxonomyPreference,
} from "api-types";

import React, { createContext, useContext } from "react";

import type { Tag } from "@dashboard/types";

/**
 * Context for taxonomy related data.
 *
 * Contains taxonomy data for the current language context,
 * used for rending allergens and dishes across various parts of the app.
 */
export type TaxonomyCtx = {
  allergenMappings: AllergenMapping[];
  taxonomyAllergens: TaxonomyAllergen[];
  taxonomyPreferences: TaxonomyPreference[];
  taxonomyDisclaimers: TaxonomyDisclaimer[];
  tags: Tag[];
};

const TaxonomyContext = createContext<TaxonomyCtx>({} as TaxonomyCtx);

export const TaxonomyProvider = ({
  children,
  allergenMappings,
  taxonomyAllergens,
  taxonomyPreferences,
  taxonomyDisclaimers,
  tags,
}: {
  children: React.ReactNode;
  allergenMappings: AllergenMapping[];
  taxonomyAllergens: TaxonomyAllergen[];
  taxonomyPreferences: TaxonomyPreference[];
  taxonomyDisclaimers: TaxonomyDisclaimer[];
  tags: Tag[];
}) => {
  return (
    <TaxonomyContext.Provider
      value={{
        allergenMappings,
        taxonomyAllergens,
        taxonomyPreferences,
        taxonomyDisclaimers,
        tags,
      }}
    >
      {children}
    </TaxonomyContext.Provider>
  );
};

export const useTaxonomyCtx: () => TaxonomyCtx = () =>
  useContext(TaxonomyContext);
